<template>
  <div class="nw-note" :class="[`nw-note--${options.align || 'left'}`, `nw-note--${options.size || 'm'}`]" :style="colorStyle">
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div v-if="html" class="nw-note-body" v-html="html"></div>
    <p v-else class="ov-empty">{{ $t('dash.overview.widgets.note_empty') }}</p>
  </div>
</template>

<script>
import { NOTE_TEXT_COLORS, resolveColor } from './noteColors'

// Échappement complet puis mise en forme limitée (aucun HTML saisi n'est interprété) :
// # titre, ## sous-titre, **gras**, *italique*, __souligné__, ==surligné==, ~~barré~~, liens http(s), listes « - » et « 1. », séparateur « --- »
const escape = s => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

function inline(line) {
  return line
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.+?)__/g, '<u>$1</u>')
    .replace(/==(.+?)==/g, '<mark>$1</mark>')
    .replace(/~~(.+?)~~/g, '<s>$1</s>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
}

function renderNote(text) {
  const lines = escape(text || '').split(/\r?\n/)
  let html = ''
  let list = null
  const closeList = () => {
    if (list) html += `</${list}>`
    list = null
  }
  lines.forEach(line => {
    const bullet = /^\s*-\s+(.*)$/.exec(line)
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line)
    if (bullet || numbered) {
      const tag = bullet ? 'ul' : 'ol'
      if (list !== tag) {
        closeList()
        html += `<${tag}>`
        list = tag
      }
      html += `<li>${inline((bullet || numbered)[1])}</li>`
      return
    }
    closeList()
    if (/^\s*---\s*$/.test(line)) html += '<hr>'
    else if (/^##\s+/.test(line)) html += `<h4>${inline(line.replace(/^##\s+/, ''))}</h4>`
    else if (/^#\s+/.test(line)) html += `<h3>${inline(line.replace(/^#\s+/, ''))}</h3>`
    else if (line.trim()) html += `<p>${inline(line)}</p>`
  })
  closeList()
  return html
}

/**
 * Texte mis en forme pour titrer, commenter ou expliquer une zone du tableau de bord
 * options : text, align (left | center | right), size (s | m | l), color (texte), titleColor (titres) ; le fond (background) est appliqué à la carte
 */
export default {
  name: 'NoteWidget',

  props: {
    options: { type: Object, default: () => ({}) }
  },

  computed: {
    // Texte et titres (# / ##) : couleur du titre, sinon celle du texte, sinon prune
    colorStyle() {
      const text = resolveColor(this.options.color, NOTE_TEXT_COLORS)
      const title = resolveColor(this.options.titleColor, NOTE_TEXT_COLORS) || text
      return { ...(text ? { color: text } : {}), ...(title ? { '--nw-accent': title } : {}) }
    },

    html() {
      return renderNote(this.options.text)
    }
  }
}
</script>

<style scoped>
.nw-note {
  --nw-accent: #6b1f4d;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  padding: 2px;
  border-radius: 10px;
  font-size: 13px;
  color: #121826;
  line-height: 1.5;
}
.nw-note--s { font-size: 12px; }
.nw-note--l { font-size: 15.5px; }
.nw-note--center { text-align: center; }
.nw-note--right { text-align: right; }
.nw-note-body >>> h3 {
  margin: 0 0 6px;
  font-size: 1.25em;
  font-weight: 700;
  color: var(--nw-accent);
}
.nw-note-body >>> h4 {
  margin: 0 0 4px;
  font-size: 1.05em;
  font-weight: 700;
  color: var(--nw-accent);
}
.nw-note-body >>> p {
  margin: 0 0 6px;
}
.nw-note-body >>> ul,
.nw-note-body >>> ol {
  margin: 0 0 6px;
  padding-left: 20px;
  text-align: left;
}
.nw-note-body >>> mark {
  padding: 0 3px;
  border-radius: 3px;
  background: #ffe9a8;
  color: inherit;
}
.nw-note-body >>> hr {
  margin: 8px 0;
  border: none;
  border-top: 1px solid #e0d0ea;
}
.nw-note-body >>> a {
  color: var(--nw-accent);
  text-decoration: underline;
}
</style>
